import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { onIdTokenChanged, signInAnonymously } from 'firebase/auth';
import db, { auth } from './firebase';
import AuthBar from './components/AuthBar';
import Header from './components/Header';
import RatingForm from './components/RatingForm';
import Leaderboard from './components/Leaderboard';
import MapSection from './components/MapSection';
import config from './config';

// Restaurants always offered as suggestions, even before anything is rated.
const SEED_RESTAURANTS = ['Wingstop', 'Buffalo Wild Wings'];

function App() {
  // Restaurant is shared: it drives the form, the search link, and the map.
  const [restaurant, setRestaurant] = useState('');
  // Bumped after each successful submit to re-read Firestore.
  const [refreshKey, setRefreshKey] = useState(0);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Track auth state. Everyone starts as an anonymous guest; if there's no
  // session (first load, or after sign-out) we create an anonymous one so
  // rating always works. onIdTokenChanged also fires on account linking, so
  // upgrading to Google is reflected immediately. Errors (e.g. a provider not
  // enabled yet) are handled again at submit time.
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, (current) => {
      setUser(current);
      if (!current) {
        signInAnonymously(auth).catch((error) =>
          console.warn('Anonymous sign-in failed:', error.code || error.message)
        );
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    let active = true;
    const fetchRatings = async () => {
      setLoading(true);
      try {
        const snapshot = await getDocs(collection(db, 'ratings'));
        if (active) setRatings(snapshot.docs.map((doc) => doc.data()));
      } catch (error) {
        console.error('Error loading ratings:', error);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchRatings();
    return () => {
      active = false;
    };
  }, [refreshKey]);

  // Leaderboard aggregation: average rating + count per restaurant.
  const leaderboard = useMemo(() => {
    const grouped = {};
    ratings.forEach(({ restaurant: name, rating }) => {
      if (!name) return;
      if (!grouped[name]) grouped[name] = { total: 0, count: 0 };
      grouped[name].total += Number(rating) || 0;
      grouped[name].count += 1;
    });
    return Object.entries(grouped)
      .map(([name, data]) => ({
        restaurant: name,
        avgRating: (data.total / data.count).toFixed(2),
        totalRatings: data.count,
      }))
      .sort((a, b) => b.avgRating - a.avgRating);
  }, [ratings]);

  // Self-propagating suggestion list: seeds + every restaurant ever rated.
  const knownRestaurants = useMemo(() => {
    const set = new Set(SEED_RESTAURANTS);
    ratings.forEach(({ restaurant: name }) => {
      if (name) set.add(name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [ratings]);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#000000',
        padding: '40px 16px',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          maxWidth: 560,
          margin: '0 auto',
          background: '#1c1c1e',
          padding: 36,
          borderRadius: 22,
          boxShadow: '0 12px 48px rgba(0,0,0,.55)',
          border: '1px solid #2c2c2e',
          boxSizing: 'border-box',
        }}
      >
        <AuthBar user={user} />
        <Header />
        <RatingForm
          restaurant={restaurant}
          setRestaurant={setRestaurant}
          restaurants={knownRestaurants}
          onSubmitted={() => setRefreshKey((k) => k + 1)}
        />
        <Leaderboard items={leaderboard} loading={loading} />
        {config.showMap && <MapSection restaurant={restaurant} />}
      </div>
    </div>
  );
}

export default App;
